import { useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Calendar, X } from "lucide-react";

import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import NotionEditor from "../../../../../shared/ui/form/input/NotionEditor";
import Button from "../../../../../shared/ui/button/Button";
import FilterSelect from "../../../../../shared/ui/form/FilterSelect";

interface SessionOption {
  value: string;
  text: string;
  date?: string;
  hour?: string;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  phase: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setPhase: (phase: any) => void;
  onSave: () => void;
  onClose: () => void;
  sessions: SessionOption[];
  isCreating: boolean;
}

export default function TimelinePhaseModal({
  phase,
  setPhase,
  onSave,
  onClose,
  sessions,
  isCreating,
}: Props) {
  const { t } = useTranslation();

  const filterOptions = useMemo(() => {
    return sessions.map((s) => ({
      value: s.value,
      label: s.text,
      subLabel: s.date ? `${s.date}${s.hour ? ' - ' + s.hour : ''}` : undefined,
    }));
  }, [sessions]);

  const handleSessionsChange = (value: string | string[]) => {
    setPhase({
      ...phase,
      sessions: Array.isArray(value) ? value : [value],
    });
  };

  return createPortal(
    <div className="fixed inset-0 z-9999999 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900 max-h-[90vh] overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-white/[0.02] sticky top-0 z-10 backdrop-blur-md">
          <h2 className="text-xl font-bold text-content-primary">
            {isCreating ? t("therapeuticPlan.createPhaseTitle") : t("therapeuticPlan.editPhaseTitle")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-white/5 transition-colors text-content-tertiary hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          {/* Title & Order */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="md:col-span-3">
              <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
                {t("therapeuticPlan.phaseTitle")}
              </Label>
              <Input
                value={phase.title}
                className="rounded-xl bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/5 focus:bg-white transition-all py-3"
                onChange={(e) => setPhase({ ...phase, title: e.target.value })}
              />
            </div>

            <div>
              <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
                {t("therapeuticPlan.order")}
              </Label>
              <Input
                type="number"
                value={phase.order ?? 0}
                className="rounded-xl bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/5 focus:bg-white transition-all py-3"
                onChange={(e) =>
                  setPhase({ ...phase, order: Number(e.target.value) })
                }
              />
            </div>
          </div>

          {/* Description */}
          <div className="pt-6 border-t border-gray-100 dark:border-white/5">
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("therapeuticPlan.description")}
            </Label>
            <div className="rounded-xl border border-gray-100 dark:border-white/5 overflow-hidden">
              <NotionEditor
                value={phase.description}
                onChange={(v) => setPhase({ ...phase, description: v })}
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div className="pt-6 border-t border-gray-100 dark:border-white/5">
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("therapeuticPlan.internalNotes")}
            </Label>
            <div className="rounded-xl border border-gray-100 dark:border-white/5 overflow-hidden">
              <NotionEditor
                value={phase.notes}
                onChange={(v) => setPhase({ ...phase, notes: v })}
              />
            </div>
          </div>

          {/* Session search */}
          <div className="pt-6 border-t border-gray-100 dark:border-white/5">
            <Label className="mb-2 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("therapeuticPlan.linkedSessions")}
            </Label>
            <FilterSelect
              options={filterOptions}
              value={phase.sessions ?? []}
              onChange={handleSessionsChange}
              multiple={true}
              placeholder={t("therapeuticPlan.searchSessionPlaceholder")}
              searchPlaceholder={t("therapeuticPlan.searchSession")}
              icon={<Calendar className="w-5 h-5" />}
              className="w-full rounded-xl bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/5 focus-within:bg-white transition-all py-1"
            />
          </div>

          {/* Status */}
          <div className="pt-6 border-t border-gray-100 dark:border-white/5">
            <Label className="mb-4 uppercase text-xs font-semibold text-content-tertiary tracking-wider font-montserrat">
              {t("common.status")}
            </Label>
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/[0.02] rounded-xl border border-gray-100 dark:border-white/5 group cursor-pointer"
              onClick={() => setPhase({ ...phase, done: !phase.done })}
            >
              <input
                type="checkbox"
                checked={phase.done}
                onChange={(e) => e.stopPropagation()}
                className="w-5 h-5 text-brand-600 bg-white border-gray-300 rounded-lg focus:ring-brand-500 transition-all cursor-pointer"
              />
              <span className="text-sm font-semibold text-content-secondary">
                {t("therapeuticPlan.markCompleted")}
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 dark:border-white/5 p-6 flex justify-end gap-3 bg-gray-50/50 dark:bg-white/[0.02] sticky bottom-0 backdrop-blur-md rounded-b-2xl">
          <Button variant="outline" onClick={onClose} className="rounded-xl px-6">
            {t("common.cancel")}
          </Button>
          <Button onClick={onSave} className="rounded-xl px-8 shadow-lg shadow-brand-500/20">
            {isCreating ? t("common.create") : t("common.save")}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
